import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { getUser, getToken, updateUser } from '@/utils/auth';
import { MailWarning, X, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function EmailVerificationBanner() {
  const user = getUser();
  const token = getToken();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [verified, setVerified] = useState(false);
  // Carries 'delivered' | 'quota_exceeded' | 'domain_unverified' | 'failed' |
  // 'config_missing' | 'already_verified' so we can render an accurate banner
  // state instead of the previous "always shows Sent" UX bug.
  const [sendStatus, setSendStatus] = useState(null);

  // Persistent fallback flag — once verification has ever succeeded on this
  // device, the banner stays hidden even if the user object is later
  // re-hydrated from a stale source.
  const everVerified =
    typeof window !== 'undefined' &&
    localStorage.getItem('safer_email_verified') === '1';

  // Check fresh status from /me in background
  useEffect(() => {
    if (!user || !token || user.email_verified || everVerified) return;
    const checkStatus = async () => {
      try {
        const res = await fetch(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.email_verified) {
            updateUser({ email_verified: true });
            try { localStorage.setItem('safer_email_verified', '1'); } catch { /* noop */ }
            setVerified(true);
          }
        }
      } catch {
        // ignore
      }
    };
    checkStatus();
  }, [user, token, everVerified]);

  // Don't show if verified, dismissed, no user, or no token
  if (!user || !token || user.email_verified || everVerified || dismissed || verified) return null;

  const handleResend = async () => {
    setSending(true);
    setSendStatus(null);
    try {
      const res = await fetch(`${API}/api/auth/send-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email })
      });
      const data = await res.json().catch(() => ({}));
      const status = data?.email_send_status || 'failed';
      setSendStatus(status);
      // Only show "Sent" for successful delivery — quota / domain / failed
      // states render their own warning UI.
      if (status === 'delivered' || status === 'already_verified') {
        setSent(true);
      }
    } catch {
      setSendStatus('failed');
    } finally {
      setSending(false);
    }
  };

  const isQuota = sendStatus === 'quota_exceeded';
  const isFailure = !!sendStatus && sendStatus !== 'delivered' && sendStatus !== 'already_verified';

  return (
    <div
      className={`border-b px-4 py-2.5 flex items-center justify-between gap-3 ${
        isQuota ? 'bg-rose-50 border-rose-200' : 'bg-amber-50 border-amber-200'
      }`}
      data-testid="email-verification-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        {isQuota ? (
          <AlertTriangle className="w-4 h-4 text-rose-600 flex-shrink-0" />
        ) : (
          <MailWarning className="w-4 h-4 text-amber-600 flex-shrink-0" />
        )}
        <p className={`text-sm truncate ${isQuota ? 'text-rose-900' : 'text-amber-800'}`}>
          {isQuota ? (
            <>
              <span className="font-medium">Email service quota reached</span>
              <span className="hidden sm:inline" data-testid="quota-exceeded-message">
                {' '}— our email provider is rate-limited today. Please contact admin@safer-i.com to verify manually.
              </span>
            </>
          ) : isFailure ? (
            <>
              <span className="font-medium">Verification email failed to send</span>
              <span className="hidden sm:inline">
                {' '}— please try again or contact admin@safer-i.com.
              </span>
            </>
          ) : (
            <>
              <span className="font-medium">Verify your email</span>
              <span className="hidden sm:inline"> — check your inbox for a verification link to unlock all features.</span>
            </>
          )}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {sent ? (
          <span className="flex items-center gap-1 text-xs text-green-700" data-testid="resend-sent-confirmation">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Sent
          </span>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={handleResend}
            disabled={sending}
            className={`h-7 text-xs ${
              isQuota
                ? 'border-rose-300 text-rose-800 hover:bg-rose-100'
                : 'border-amber-300 text-amber-800 hover:bg-amber-100'
            }`}
            data-testid="resend-verification-btn"
          >
            {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            {isFailure ? 'Try again' : 'Resend'}
          </Button>
        )}
        <button
          onClick={() => setDismissed(true)}
          className={`p-0.5 ${isQuota ? 'text-rose-500 hover:text-rose-700' : 'text-amber-500 hover:text-amber-700'}`}
          data-testid="dismiss-verification-banner"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
